import type { Conversation } from '@nucleus-commerce/api-client/admin';

export type SlaTarget = 'firstResponse' | 'resolution';

/**
 * Where one ticket target stands: `due` counts down to `at`, `overdue` passed `at` while still
 * running, `late` was met after `at` (or the ticket closed after it), `paused` waits on the shopper
 * or is closed, `met` finished in time.
 */
export interface SlaClock {
  target: SlaTarget;
  state: 'due' | 'overdue' | 'late' | 'paused' | 'met';
  at: string | null;
}

type Ticket = Pick<Conversation, 'status' | 'firstStaffReplyAt' | 'sla'>;

/** Both targets of a ticket, in the order they come due; empty for chats and stores without SLA. */
export function slaClocks(conversation: Ticket, now: number): SlaClock[] {
  const sla = conversation.sla;
  if (!sla) return [];
  const clocks: SlaClock[] = [];
  const passed = (at: string) => Date.parse(at) <= now;
  if (sla.firstResponseDueAt) {
    const due = sla.firstResponseDueAt;
    if (sla.firstResponseBreachedAt) {
      clocks.push({
        target: 'firstResponse',
        state: conversation.firstStaffReplyAt ? 'late' : 'overdue',
        at: due,
      });
    } else if (conversation.firstStaffReplyAt) {
      clocks.push({ target: 'firstResponse', state: 'met', at: due });
    } else if (conversation.status === 'closed') {
      clocks.push({ target: 'firstResponse', state: 'paused', at: due });
    } else {
      clocks.push({ target: 'firstResponse', state: passed(due) ? 'overdue' : 'due', at: due });
    }
  }
  if (sla.resolutionDueAt) {
    const due = sla.resolutionDueAt;
    const closed = conversation.status === 'closed';
    if (sla.resolutionBreachedAt) {
      clocks.push({ target: 'resolution', state: closed ? 'late' : 'overdue', at: due });
    } else if (closed) {
      clocks.push({ target: 'resolution', state: 'met', at: due });
    } else if (sla.resolutionPausedAt) {
      clocks.push({ target: 'resolution', state: 'paused', at: due });
    } else {
      clocks.push({ target: 'resolution', state: passed(due) ? 'overdue' : 'due', at: due });
    }
  }
  return clocks;
}

/** The one clock worth showing on a row: anything overdue, else the next running deadline. */
export function headlineClock(clocks: SlaClock[]): SlaClock | null {
  return (
    clocks.find((clock) => clock.state === 'overdue') ??
    clocks.find((clock) => clock.state === 'due') ??
    clocks.find((clock) => clock.state === 'paused') ??
    null
  );
}

export interface DurationParts {
  days: number;
  hours: number;
  minutes: number;
}

/** Whole minutes between two instants, split for "2d 4h" / "3h 10m" / "12m" labels. */
export function durationParts(fromMs: number, toMs: number): DurationParts {
  const minutes = Math.max(0, Math.floor(Math.abs(toMs - fromMs) / 60_000));
  return {
    days: Math.floor(minutes / 1440),
    hours: Math.floor((minutes % 1440) / 60),
    minutes: minutes % 60,
  };
}
